import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import {
  type Identifier,
  useDataProvider,
  useGetIdentity,
  useGetList,
  useGetOne,
  useNotify,
  useTranslate,
} from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

import { ListErrorState } from "../design/ListStates";
import { StatusPill } from "../design/StatusPill";
import type { CrmDataProvider } from "../providers/types";
import type {
  Contact,
  Deal,
  DealActivityResult,
  DealActivityType,
  DealNote,
  DealStageActivity,
  Project,
} from "../types";
import { ActivityGroupPicker } from "./ActivityGroupPicker";
import {
  attemptsExhaustedMessage,
  computeAttemptInfo,
  computeNextStepPreview,
  formatAttemptLabel,
  orderActivitiesForStage,
  orderResultsForActivity,
} from "./activityLogRules";
import { MessageBlock } from "./MessageBlock";
import { NextStepPreviewBlock } from "./NextStepPreviewBlock";
import { PickerButton } from "./PickerButton";

const NOTE_MAX_LENGTH = 2000;
const CATALOGUE_PAGINATION = { page: 1, perPage: 1000 } as const;
const CATALOGUE_SORT = { field: "position", order: "ASC" as const };

export interface LogActivityFormProps {
  dealId: Identifier;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Almikia Epic EA (TASK-111), vision #4: "actividad + resultado con
 * opciones; mensaje sugerido por actividad; siguiente acción automática" --
 * a one-screen sheet that picks the activity (the lead's stage activities
 * first), shows its copyable message, picks the result, previews the
 * proposed next step, and saves with a single `logDealActivity` call. The
 * only structural requirement (per the ticket) is that it opens for a given
 * lead id from anywhere -- the lead show mounts it today, Hoy (TASK-112)
 * mounts it on mobile tomorrow, unchanged.
 */
export function LogActivityForm({
  dealId,
  open,
  onOpenChange,
}: LogActivityFormProps) {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { identity } = useGetIdentity();

  const [activityTypeId, setActivityTypeId] = useState<Identifier | null>(null);
  const [activityResultId, setActivityResultId] = useState<Identifier | null>(
    null,
  );
  const [note, setNote] = useState("");
  const [nextDueAtLocal, setNextDueAtLocal] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Almikia design-system.md section 8 ("vacío, cargando y error diseñados"):
  // reset every field when the sheet closes so it never reopens mid-way
  // through a previous lead's entry.
  useEffect(() => {
    if (!open) {
      setActivityTypeId(null);
      setActivityResultId(null);
      setNote("");
      setNextDueAtLocal("");
      setSubmitError(null);
    }
  }, [open]);

  const { data: deal, isPending: dealPending } = useGetOne<Deal>(
    "deals",
    { id: dealId },
    { enabled: open },
  );
  const firstContactId = deal?.contact_ids?.[0];
  const { data: contact } = useGetOne<Contact>(
    "contacts",
    { id: firstContactId as Identifier },
    { enabled: open && firstContactId != null },
  );
  const { data: project } = useGetOne<Project>(
    "projects",
    { id: deal?.project_id as Identifier },
    { enabled: open && deal?.project_id != null },
  );

  const {
    data: activityTypes = [],
    isPending: activityTypesPending,
    error: activityTypesError,
    refetch: refetchActivityTypes,
  } = useGetList<DealActivityType>(
    "deal_activity_types",
    { filter: {}, pagination: CATALOGUE_PAGINATION, sort: CATALOGUE_SORT },
    { enabled: open },
  );
  const {
    data: activityResults = [],
    isPending: activityResultsPending,
    error: activityResultsError,
    refetch: refetchActivityResults,
  } = useGetList<DealActivityResult>(
    "deal_activity_results",
    { filter: {}, pagination: CATALOGUE_PAGINATION, sort: CATALOGUE_SORT },
    { enabled: open },
  );
  const { data: stageActivities = [] } = useGetList<DealStageActivity>(
    "deal_stage_activities",
    {
      filter: {},
      pagination: CATALOGUE_PAGINATION,
      sort: { field: "id", order: "ASC" },
    },
    { enabled: open },
  );
  const { data: priorNotes = [], isPending: priorNotesPending } =
    useGetList<DealNote>(
      "deal_notes",
      {
        filter: { deal_id: dealId, "activity_type_id@not.is": null },
        pagination: { page: 1, perPage: 10_000 },
        sort: { field: "id", order: "ASC" },
      },
      { enabled: open },
    );

  const groups = useMemo(
    () => orderActivitiesForStage(activityTypes, stageActivities, deal?.stage),
    [activityTypes, stageActivities, deal?.stage],
  );
  const activityType = activityTypes.find((a) => a.id === activityTypeId);
  const results = useMemo(
    () =>
      activityTypeId != null
        ? orderResultsForActivity(activityResults, activityTypeId)
        : [],
    [activityResults, activityTypeId],
  );
  const result = activityResults.find((r) => r.id === activityResultId);

  const priorActivityTypeIds = useMemo(
    () =>
      priorNotes
        .map((n) => n.activity_type_id)
        .filter((id): id is Identifier => id != null),
    [priorNotes],
  );

  const existingEntryCountForActivity =
    activityTypeId != null
      ? priorActivityTypeIds.filter((id) => id === activityTypeId).length
      : 0;
  const attemptInfo = activityType
    ? computeAttemptInfo(
        activityType.max_attempts,
        existingEntryCountForActivity,
      )
    : null;

  // `now` is frozen to the moment a result is picked, not re-read on every
  // render: `computeNextStepPreview`'s default `now = new Date()` would
  // otherwise make `dueAt` a new timestamp on every keystroke (e.g. typing
  // the note), re-triggering the prefill effect below and overwriting
  // whatever due date/time the advisor had already edited.
  const previewNow = useMemo(() => new Date(), [activityResultId]);
  const nextStepPreview = useMemo(
    () =>
      result && activityTypeId != null
        ? computeNextStepPreview({
            result,
            activityTypes,
            leadHasContact: !!deal?.contact_ids?.length,
            priorActivityTypeIds: [...priorActivityTypeIds, activityTypeId],
            now: previewNow,
          })
        : null,
    [
      result,
      activityTypeId,
      activityTypes,
      deal?.contact_ids?.length,
      priorActivityTypeIds,
      previewNow,
    ],
  );

  // Prefill the editable due date/time once per result selection; keyed on
  // `activityResultId` (not on `nextStepPreview.dueAt`, which would refire on
  // every render and stomp the advisor's edited value) so it never resets
  // the field while the advisor keeps typing.
  useEffect(() => {
    if (nextStepPreview?.status === "created" && nextStepPreview.dueAt) {
      setNextDueAtLocal(
        format(new Date(nextStepPreview.dueAt), "yyyy-MM-dd'T'HH:mm"),
      );
    } else {
      setNextDueAtLocal("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityResultId, nextStepPreview?.status]);

  const placeholderValues = {
    nombre: contact?.first_name,
    asesora: identity?.fullName?.split(" ")[0] ?? null,
    proyecto: project?.name,
  };

  const { mutate, isPending: isSaving } = useMutation({
    mutationFn: () =>
      dataProvider.logDealActivity({
        dealId,
        activityTypeId: activityTypeId as Identifier,
        activityResultId: activityResultId as Identifier,
        note: note.trim() || undefined,
        nextDueAt: nextDueAtLocal
          ? new Date(nextDueAtLocal).toISOString()
          : undefined,
      }),
    onSuccess: () => {
      // `LogDealActivityResult`'s fields (taskId, closedTaskIds, nextStep)
      // drive no further UI here: the invalidated `tasks`/`deal_notes`
      // queries are what `DealNextStep` and the notes trail re-render from.
      // Almikia Epic ER (TASK-007, retry round): a logged activity can also
      // resolve a `lead_alerts` row (TASK-005's engine), so the lead page's
      // `LeadAlertPills` (`useDealLeadAlerts` -> `useGetList("lead_alerts")`)
      // must refetch too, or a resolved pill only clears on a manual reload.
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["deal_notes"] });
      queryClient.invalidateQueries({ queryKey: ["lead_alerts"] });
      notify("resources.deals.activity_log.success", {
        type: "info",
        messageArgs: { _: "Actividad registrada" },
      });
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      setSubmitError(
        error instanceof Error
          ? error.message
          : translate("resources.deals.activity_log.save_error", {
              _: "No se pudo registrar la actividad",
            }),
      );
    },
  });

  const isLoading =
    dealPending ||
    activityTypesPending ||
    activityResultsPending ||
    priorNotesPending;
  const catalogueError = activityTypesError || activityResultsError;
  const isCapExhausted = !!attemptInfo?.exhausted;
  const canSave =
    activityTypeId != null &&
    activityResultId != null &&
    note.length <= NOTE_MAX_LENGTH &&
    !isCapExhausted &&
    !isSaving;

  const handleSelectActivity = (id: Identifier) => {
    setActivityTypeId(id);
    setActivityResultId(null);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="log-activity-sheet"
        side="bottom"
        className="h-dvh flex flex-col [&_[data-slot=sheet-close]]:text-sidebar-foreground"
        aria-describedby={undefined}
      >
        <SheetHeader className="bg-sidebar text-sidebar-foreground border-b">
          <SheetTitle className="text-sidebar-foreground text-xl font-semibold">
            {translate("resources.deals.activity_log.title", {
              _: "Registrar actividad",
            })}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto flex flex-col gap-4 p-4 max-w-[390px] w-full mx-auto">
          {isLoading ? (
            <div aria-busy="true" role="status" className="space-y-3">
              <span className="sr-only">{translate("crm.common.loading")}</span>
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : catalogueError ? (
            <ListErrorState
              message={translate("resources.deals.activity_log.error_loading", {
                _: "No se pudieron cargar las actividades",
              })}
              onRetry={() => {
                refetchActivityTypes();
                refetchActivityResults();
              }}
            />
          ) : groups.every((g) => g.activities.length === 0) ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              {translate("resources.deals.activity_log.empty", {
                _: "No hay actividades disponibles",
              })}
            </p>
          ) : (
            <>
              <ActivityGroupPicker
                groups={groups}
                selectedId={activityTypeId}
                onSelect={handleSelectActivity}
              />

              {activityType ? (
                <MessageBlock
                  template={activityType.message_template}
                  values={placeholderValues}
                />
              ) : null}

              {attemptInfo ? (
                isCapExhausted ? (
                  <StatusPill
                    tone="not-ok"
                    text={attemptsExhaustedMessage(
                      activityType!.name,
                      attemptInfo.cap!,
                    )}
                  />
                ) : (
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {formatAttemptLabel(attemptInfo)}
                  </span>
                )
              ) : null}

              {activityTypeId != null && !isCapExhausted ? (
                <fieldset className="flex flex-col gap-2">
                  <legend className="text-sm font-medium mb-1">
                    {translate("resources.deals.activity_log.result_label", {
                      _: "Resultado",
                    })}
                  </legend>
                  {results.map((r) => (
                    <PickerButton
                      key={r.id}
                      selected={activityResultId === r.id}
                      onClick={() => setActivityResultId(r.id)}
                    >
                      {r.label}
                    </PickerButton>
                  ))}
                </fieldset>
              ) : null}

              {result ? (
                <MessageBlock
                  template={result.message_template}
                  values={placeholderValues}
                />
              ) : null}

              {nextStepPreview ? (
                <NextStepPreviewBlock
                  status={nextStepPreview.status}
                  nextActivityName={
                    activityTypes.find(
                      (a) => a.id === nextStepPreview.nextActivityTypeId,
                    )?.name
                  }
                  dueAtLocal={nextDueAtLocal}
                  onDueAtLocalChange={setNextDueAtLocal}
                />
              ) : null}

              <div className="flex flex-col gap-1">
                <label
                  htmlFor="activity-log-note"
                  className="text-sm font-medium"
                >
                  {translate("resources.deals.activity_log.note_label", {
                    _: "Nota (opcional)",
                  })}
                </label>
                <Textarea
                  id="activity-log-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  maxLength={NOTE_MAX_LENGTH}
                  className="text-[15px]"
                />
                {note.length > NOTE_MAX_LENGTH - 200 ? (
                  <span className="text-xs text-muted-foreground tabular-nums self-end">
                    {note.length}/{NOTE_MAX_LENGTH}
                  </span>
                ) : null}
              </div>

              {submitError ? (
                <p role="alert" className="text-sm text-destructive">
                  {submitError}
                </p>
              ) : null}
            </>
          )}
        </div>

        <SheetFooter className="border-t flex flex-row w-full gap-4">
          <Button
            type="button"
            variant="gradient"
            className="flex-1 h-12 text-[15px]"
            disabled={!canSave}
            onClick={() => {
              setSubmitError(null);
              mutate();
            }}
          >
            {translate("resources.deals.activity_log.save", {
              _: "Guardar",
            })}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
